import React, { useState, useEffect, useRef } from "react";
import _ from "lodash";
import {
  _findSelectOption,
  _capitalized,
  _isFirstRender,
  _getSkipByPage,
  _getCSRFTokenValue,
  _getTranslation,
  _getPlaceHolderKey,
} from "../../utils/helper";
import api from "../../utils/api";

import { Loader, Pagination } from "../../atoms";
import { CardPeople } from "../../cards";
import { InputSearch, InputSelect, MobileFilters } from "../../forms";

const INITIAL_SORT = "hierarchy";
const SORTING_OPTIONS = [
  {
    label: _getTranslation("hierarchically"),
    value: "hierarchy",
  },
  {
    label: _getTranslation("alphabetically"),
    value: "alphabetically",
  },
];

const PeopleOverview = (props) => {
  const language = props?.data["language"] || "en";
  
  const INITIAL_VALUES = {
    searchTerm: "",
    region: "",
    theme: "",
    page: 1,
    language: language,
  };
  const filterListOrder = ["region", "theme"];
  const [values, setValues] = useState(INITIAL_VALUES);
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_VALUES);
  const [sortby, setSortby] = useState(INITIAL_SORT);
  const prevSortbyRef = useRef(INITIAL_SORT);
  const [posts, setPosts] = useState([]);
  const [filterOptions, setFilterOptions] = useState([]);
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);
  const [isLoadingPosts, setIsLoadingPosts] = useState(false);
  const [areFiltersActive, setAreFiltersActive] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const isFirstLoad = _isFirstRender();

  useEffect(() => {
    fetchFilters();
    fetchPosts();
  }, []);

  useEffect(() => {
    if (!isFirstLoad) {
      // sync the values when filters are applied
      setValues(appliedFilters);

      fetchPosts();
    }
  }, [appliedFilters]);

  useEffect(() => {
    if (prevSortbyRef.current !== sortby) {
      fetchPosts();
    }
    prevSortbyRef.current = sortby;
  }, [sortby]);

  const fetchFilters = () => {
    setIsLoadingFilters(true);
    api
      .get("/filterlistoptions?lang=" + document.getElementById("currentLangCode").innerHTML)
      .then((res) => {
        setIsLoadingFilters(false);
        const filtersData = res.data;
        const formattedList = [];
        Object.keys(filtersData).map((key) => {
          formattedList.push({
            label: _capitalized(key),
            key: key,
            options: filtersData[key],
          });
        });
        setFilterOptions(formattedList);
      })
      .catch(() => {
        setIsLoadingFilters(false);
      });
  };

  const fetchPosts = async () => {
    setIsLoadingPosts(true);
    const POSTS_PER_PAGE = 20;
    const POSTS_SKIP = _getSkipByPage(appliedFilters.page, POSTS_PER_PAGE);

    const fields = {
      ...appliedFilters,
      orderBy: sortby,
      skip: POSTS_SKIP,
      take: POSTS_PER_PAGE,
    };

    const csrfToken = await _getCSRFTokenValue();
    api
      .post("/people", fields, {
        headers: {
          "X-CSRF-Token": csrfToken,
        },
      })
      .then((res) => {
        const TOTAL_POSTS = res.data.totalNodes;
        const TOTAL_PAGES = Math.ceil(TOTAL_POSTS / POSTS_PER_PAGE);
        setIsLoadingPosts(false);
        setPosts(res.data.contentData);
        setTotalPages(TOTAL_PAGES);
      })
      .catch((err) => {
        setIsLoadingPosts(false);
      });
  };

  const FilterInputs = () => {
    return isLoadingFilters ? (
      <Loader />
    ) : (
      <React.Fragment>
        <div className="filter-item flex flex-col sm:flex-row sm:items-center gap-y-3 gap-x-4">
          <label className="hidden sm:block font-semibold">
            {_getTranslation("filter_by")}
          </label>

          {filterListOrder.map((key) => {
            const foundFilterOptions = filterOptions.find(
              (opt) => opt.key === key
            );

            return (
              foundFilterOptions && (
                <InputSelect
                  class="flex-1 lg:w-[160px]"
                  name={foundFilterOptions.key}
                  // placeholder={`All ${foundFilterOptions.key}s`}
                  // placeholder={`All ${_getTranslation((foundFilterOptions.key + 's').toLowerCase())}`}
                  placeholder={`${_getTranslation(_getPlaceHolderKey(foundFilterOptions.key))}`}
                  options={foundFilterOptions.options}
                  isClearable
                  isSearchable
                  value={_findSelectOption(
                    foundFilterOptions.options,
                    values[foundFilterOptions.key]
                  )}
                  onChange={(name, selectedValues) => {
                    setValues({
                      ...values,
                      [foundFilterOptions.key]: selectedValues,
                    });
                  }}
                />
              )
            );
          })}
        </div>

        <div className="grid grid-cols-2 sm:flex items-center gap-y-2 gap-x-4 lg:gap-x-2 sm:mt-1 lg:mt-0">
          <button className="btn btn-s--pill" onClick={() => applyFilters()}>
            {_getTranslation("apply_filters")}
          </button>
          {!_.isEqual(values, INITIAL_VALUES) && (
            <button
              className="btn btn-s--pill btn-c--hollow---black"
              onClick={() => resetFilters()}
            >
              {_getTranslation("reset_filters")}
            </button>
          )}
        </div>
      </React.Fragment>
    );
  };

  const applyFilters = (pageNo = 1, isFiltersActive = true) => {
    if (isFiltersActive) {
      setAreFiltersActive(true);
    }
    setAppliedFilters({ ...values, page: pageNo });
  };

  const resetFilters = () => {
    setSortby(INITIAL_SORT);
    setValues(INITIAL_VALUES);
    setAppliedFilters(INITIAL_VALUES);
    setAreFiltersActive(false);
  };

  return (
    <div className="mb-16">
      <h2 className="h2 !mb-4">{props?.data["page-title"]}</h2>

      <div className="desktop-filters hidden sm:flex flex-col lg:flex-row lg:items-center flex-wrap gap-y-2 gap-x-4 mb-8">
        <div className="filter-item flex-1 lg:flex-initial">
          <InputSearch
            class="w-full"
            placeholder={_getTranslation("type_in_your_keywords")}
            value={values.searchTerm}
            onChange={(e) => {
              const value = e.target.value;
              setValues({ ...values, searchTerm: value });
            }}
            onKeyUp={(e) => { if (e.key === 'Enter') { applyFilters(); } }}
          />
        </div>
        <FilterInputs />
        <div className="filter-item flex flex-col sm:flex-row sm:items-center gap-y-3 gap-x-4 ml-auto">
          <label className="hidden sm:block font-semibold">
            {_getTranslation("sort")}
          </label>
          <InputSelect
            name="sorting-order"
            class="min-w-[180px] flex-1"
            options={SORTING_OPTIONS}
            isSort
            isSearchable
            value={_findSelectOption(SORTING_OPTIONS, sortby)}
            onChange={(name, selectedValues) => {
              setSortby(selectedValues);
            }}
          />
        </div>
      </div>

      <MobileFilters class="w-full sm:hidden mb-4">
        <div className="filter-item">
          <InputSearch
            class="w-full"
            placeholder={_getTranslation("type_in_your_keywords")}
            value={values.searchTerm}
            onChange={(e) => {
              const value = e.target.value;
              setValues({ ...values, searchTerm: value });
            }}
          />
        </div>
        <FilterInputs />
      </MobileFilters>

      <div className="flex items-center justify-end gap-y-3 gap-x-4 sm:hidden mb-6">
        <label className="text-sm font-semibold">
          {_getTranslation("sort")}
        </label>
        <InputSelect
          name="sorting-order"
          class="w-[200px] max-w-full"
          options={SORTING_OPTIONS}
          isSort
          isSearchable
          value={_findSelectOption(SORTING_OPTIONS, sortby)}
          onChange={(name, selectedValues) => {
            setSortby(selectedValues);
          }}
        />
      </div>

      {isLoadingPosts && <Loader class="mb-8" />}

      {_.isArray(posts) && posts.length > 0 ? (
        <React.Fragment>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-6 lg:gap-10 xl:gap-12 mb-8">
            {posts.map((post) => (
              <CardPeople data={post} />
            ))}
          </div>

          <div className="flex items-center justify-center gap-x-6 mt-8">
            <Pagination
              page={appliedFilters.page}
              totalPages={totalPages}
              setPage={(newPage) => {
                if (areFiltersActive) {
                  applyFilters(newPage);
                } else {
                  applyFilters(newPage, false);
                }
              }}
            />
            {isLoadingPosts && <Loader />}
          </div>
        </React.Fragment>
      ) : isLoadingPosts ? null : (
        <p>{_getTranslation("there_are_no_posts_available")}.</p>
      )}
    </div>
  );
};

export default PeopleOverview;
